<template>
    <div class="customer__form modal modal--default">
        <div class="modal__panel modal__panel--leads-transfer">
            <div class="modal__logo-wrapper">
                <div class="modal__logo"></div>
            </div>

            <div class="modal__main-icon modal__main-icon--leads"></div>
            <div class="modal__close" @click="closeModal()"></div>

            <div class="form ">
                <div class="navigators">
                    <div class="navigator navigator--active">
                        <div class="navigator__bullet"></div>
                        <div class="navigator__title">
                            Transfer Leads
                        </div>
                    </div>
                </div>

                <div class="add__form ">
                    <transition name="fade" mode="out-in">
                        <Loading v-if="loading" />
                        <div v-else>
                            <div class="form__row">
                                <div class="form__field">
                                    <VSelect class="select select--default" :class="{ 'select--error': teamHasError }"
                                        :options="userTeams" label="name" :reduce="(team) => team.id"
                                        @input="handleTeamChange($event)"
                                        :placeholder="$t('settingsPage.selectTeamPlaceholder')" v-model="teamId" />
                                    <div class="form__error" v-if="teamHasError">
                                        {{ $t(`addTeamModal.${"teamIdErrorMsg"}`) }}
                                    </div>
                                </div>
                            </div>
                            <div class="form__row" v-if="teamId">
                                <div class="form__field">
                                    <VSelect class="select select--default" :options="agents"
                                        :class="{ 'select--error': agentHasError }" :reduce="(agent) => agent.id"
                                        :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`"
                                        :placeholder="$t('policyProvision.agent')" v-model="agentId" />
                                    <div class="form__error" v-if="agentHasError">
                                        {{ $t('addTransaction.agentErrorMsg') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading">
            </div>

            <button v-else type="button" class="modal__button modal__button--submit button button--default"
                @click="submitTransfer()">
                <span>Transfer</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import Loading from "@/partials/Loading";

export default {
    name: "TransferLeadsModal",
    components: {
        Loading,
    },

    data() {
        return {
            teamId: null,
            agentId: null,
            loading: false,
            teamHasError: false,
            agentHasError: false,
            isRequesting: false,
        };
    },

    watch: {
        teamId(value) {
            if (value) {
                this.teamHasError = false;
            }
        },
        agentId(value) {
            if (value) {
                this.agentHasError = false;
            }
        },
    },

    async mounted() {
        if (!this.$checkRolePermission('customer_create')) {
            this.closeModal();
        } else {
            this.loading = true;
            await this.$store.dispatch("getUserForms");
            this.loading = false;
        }
    },

    computed: {
        ...mapGetters([
            "userForms",
            "loggedUser",
            "searchLeadForms",
            "leadsToTransfer"
        ]),

        userTeams() {
            if (!this.loggedUser || !this.loggedUser.teams) return [];
            return this.loggedUser.teams;
        },

        agents() {
            if (!this.searchLeadForms || !this.searchLeadForms.agents) return [];
            return this.searchLeadForms.agents;
        },

        isFormValid() {
            return this.teamId && this.agentId && this.leadsToTransfer.length > 0;
        },
    },

    methods: {
        async handleTeamChange(teamId) {
            this.agentId = null;  
            if (teamId) {
                this.loading = true;
                try {
                    await this.$store.dispatch("getSearchLeadForms", [teamId]);
                } catch (error) {
                    console.error("Error loading team data:", error);
                }
                this.loading = false;
            }
        },

        validateForm() {
            let isValid = true;

            if (!this.teamId) {
                this.teamHasError = true;
                isValid = false;
            }

            if (!this.agentId) {
                this.agentHasError = true;
                isValid = false;
            }

            if (this.leadsToTransfer.length === 0) {
                this.$store.commit("setToast", {
                    display: true,
                    type: "error",
                    title: this.$t("transferLeadsModal.error") || "Error",
                    message: this.$t("transferLeadsModal.noLeadsSelected") || "No leads selected for transfer",
                });
                isValid = false;
            }

            return isValid;
        },

        async submitTransfer() {
            if (!this.validateForm()) {
                return;
            }

            this.isRequesting = true;

            try {
                await this.$store.dispatch("transferLeadCustomers", {
                    customerIds: this.leadsToTransfer,
                    teamId: this.teamId,
                    agentId: this.agentId,
                }); 

                this.closeModal();

            } catch (error) {
                console.error("Error transferring leads:", error); 
            } finally {
                this.isRequesting = false;
            }
        },

        closeModal() { 
            this.teamId = null;
            this.agentId = null;
            this.teamHasError = false;
            this.agentHasError = false;
            this.$store.commit("setLeadsToTransfer", []);
            this.$store.commit('setShowTransferLeadsModal', false);
        },
    },
};
</script>

<style scoped lang="scss">
.modal__button--submit {
    width: 150px;
}

.modal__panel--leads-transfer {
    width: 600px !important;
}

.transfer-info {
    padding: 15px 20px;
    margin: 15px 0;
    background: var(--bg-3);
    border-radius: var(--radius-1);
    border-left: 4px solid var(--primary);

    p {
        margin: 0;
        color: var(--text-1);
        font-size: 14px;

        strong {
            color: var(--primary);
            font-size: 16px;
        }
    }
}

.form__label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-1);

    .required {
        color: var(--error);
        margin-left: 2px;
    }
}

.textarea {
    width: 100%;
    resize: vertical;
    min-height: 80px;
    padding: 12px;
    font-family: inherit;
    line-height: 1.5;

    &::placeholder {
        color: var(--text-7);
    }
}

.form__field {
    width: 100% !important;
}

.form__row {
    margin: 20px 0;
}

.button--default:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
        background: var(--primary);
    }
}
</style>